import React, { useCallback } from 'react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  DEFAULT_NAMESPACE,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import CachedIcon from '@material-ui/icons/Cached';
import DocsIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import { Link, HeaderIconLinkRow, MarkdownContent } from '@backstage/core-components';
import { useApi, alertApiRef, errorApiRef, useRouteRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { AboutCardProps, AboutField, EntityAboutCard } from '@backstage/plugin-catalog';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import {
  catalogApiRef,
  getEntityRelations,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { scmIntegrationsApiRef, ScmIntegrationIcon } from '@backstage/integration-react';
import { Entity } from '@backstage/catalog-model';
import { formatSystemStatus, getSystemStatusChipColor } from './systemStatus';

const useCardStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
});

const useContentStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
});

const SystemAboutContent = ({ entity }: { entity: Entity }) => {
  const classes = useContentStyles();
  const { t } = useTranslationRef(catalogTranslationRef);

  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });

  const status =
    typeof entity?.spec?.status === 'string' ? entity.spec.status : undefined;
  const typeValue = typeof entity?.spec?.type === 'string' ? entity.spec.type : undefined;
  const lifecycleValue =
    typeof entity?.spec?.lifecycle === 'string' ? entity.spec.lifecycle : undefined;

  const description =
    entity?.metadata?.description ||
    t('aboutCard.descriptionField.value' as any, {
      defaultValue: 'No description',
    });

  const formattedStatus = formatSystemStatus(status);
  const statusLabel =
    formattedStatus ?? t('aboutCard.statusField.value' as any, { defaultValue: 'Unknown' });

  return (
    <Grid container>
      <AboutField
        label={t('aboutCard.descriptionField.label' as any, {
          defaultValue: 'Description',
        })}
        gridSizes={{ xs: 12 }}
      >
        <MarkdownContent className={classes.description} content={description} />
      </AboutField>

      <AboutField
        label={t('aboutCard.ownerField.label' as any, { defaultValue: 'Owner' })}
        value={t('aboutCard.ownerField.value' as any, { defaultValue: 'No owner' })}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {ownedByRelations.length > 0 && (
          <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
        )}
      </AboutField>

      <AboutField
        label={t('aboutCard.domainField.label' as any, { defaultValue: 'Domain' })}
        value={t('aboutCard.domainField.value' as any, { defaultValue: 'No domain' })}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {partOfDomainRelations.length > 0 && (
          <EntityRefLinks entityRefs={partOfDomainRelations} defaultKind="domain" />
        )}
      </AboutField>

      <AboutField
        label={t('aboutCard.typeField.label' as any, { defaultValue: 'Type' })}
        value={typeValue}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      />

      <AboutField
        label={t('aboutCard.lifecycleField.label' as any, { defaultValue: 'Lifecycle' })}
        value={lifecycleValue}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      />

      <AboutField
        label={t('aboutCard.statusField.label' as any, { defaultValue: 'Status' })}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        <Chip label={statusLabel} color={getSystemStatusChipColor(status)} size="small" />
      </AboutField>

      <AboutField
        label={t('aboutCard.tagsField.label' as any, { defaultValue: 'Tags' })}
        value={t('aboutCard.tagsField.value' as any, { defaultValue: 'No tags' })}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {(entity?.metadata?.tags || []).map(tag => (
          <Chip key={tag} size="small" label={tag} style={{ marginRight: 4 }} />
        ))}
      </AboutField>
    </Grid>
  );
};

const SystemAboutCard = ({ variant }: AboutCardProps) => {
  const classes = useCardStyles();
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const techdocsRoute = useRouteRef(techdocsPlugin.routes.docRoot);
  const { allowed: canRefresh } = useEntityPermission(catalogEntityRefreshPermission);
  const { t } = useTranslationRef(catalogTranslationRef);

  const entitySourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const entityMetadataEditUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];

  let techdocsRef;
  if (entity.metadata.annotations?.['backstage.io/techdocs-entity']) {
    try {
      techdocsRef = parseEntityRef(entity.metadata.annotations['backstage.io/techdocs-entity']);
    } catch {
      techdocsRef = undefined;
    }
  }

  const viewInSource = {
    label: t('aboutCard.viewSource' as any, { defaultValue: 'View Source' }),
    disabled: !entitySourceLocation,
    icon: <ScmIntegrationIcon type={entitySourceLocation?.integrationType} />,
    href: entitySourceLocation?.locationTargetUrl,
  };

  const viewInTechDocs = {
    label: t('aboutCard.viewTechdocs' as any, { defaultValue: 'View TechDocs' }),
    disabled: !techdocsRoute,
    icon: <DocsIcon />,
    href:
      techdocsRoute &&
      techdocsRoute({
        namespace: techdocsRef?.namespace || entity.metadata.namespace || DEFAULT_NAMESPACE,
        kind: techdocsRef?.kind || entity.kind,
        name: techdocsRef?.name || entity.metadata.name,
      }),
  };

  const subHeaderLinks = [viewInSource, viewInTechDocs];

  let cardClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
  }

  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardContentClass = classes.fullHeightCardContent;
  }

  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: t('aboutCard.refreshScheduledMessage' as any, {
          defaultValue: 'Refresh scheduled',
        }),
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi, t]);

  return (
    <Card className={cardClass}>
      <CardHeader
        title={t('aboutCard.title' as any, { defaultValue: 'About' })}
        action={
          <>
            {allowRefresh && canRefresh && (
              <IconButton
                aria-label="Refresh"
                title={t('aboutCard.refreshButtonTitle' as any, {
                  defaultValue: 'Schedule entity refresh',
                })}
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            <IconButton
              component={Link}
              aria-label="Edit"
              disabled={!entityMetadataEditUrl}
              title={t('aboutCard.editButtonTitle' as any, { defaultValue: 'Edit entity' })}
              to={entityMetadataEditUrl ?? '#'}
            >
              <EditIcon />
            </IconButton>
          </>
        }
        subheader={<HeaderIconLinkRow links={subHeaderLinks} />}
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <SystemAboutContent entity={entity} />
      </CardContent>
    </Card>
  );
};

export const CustomAboutCard = (props: AboutCardProps) => {
  const { entity } = useEntity();
  const isSystem = entity.kind?.toLocaleLowerCase('en-US') === 'system';

  if (isSystem) {
    return <SystemAboutCard {...props} />;
  }

  return <EntityAboutCard {...props} />;
};
